import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileText, Loader2, HelpCircle } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setData as OldConventionalData,
  setCurrentTab as OldConventionalCurrentTab,
  setUserInitiatedNav as OldConventionalUserInitiatedNav,
} from "../../../store/slices/oldConventionalSlice";
import { useLocation, useNavigate } from "react-router-dom";
import { Textarea } from "@/components/ui/textarea";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import FilePreview from "../../../components/common/FilePreview";
import { MultiSelect } from "../../../components/ui/multiselect";

const particularsSchema = z
  .object({
    is_tie_up_made: z.string().min(1, "This field is required"),
    tie_up_types: z.array(z.string()).optional(),
    export_oriented: z.string().min(1, "Export Oriented is required"),
    type_of_product_exported: z.string().optional(),
    backward_raw_material_source: z.string().optional(),
    backward_labour_availability: z.string().optional(),
    turnover: z.string().min(1, "Annual Turnover is required"),
    cluster_approach: z.string().min(1, "Cluster Approach is required"),
    willingness_to_switch: z
      .string()
      .min(1, "Willingness to Switch is required"),
    willingness_description: z.string().optional(),
    other_variety_specify: z.string().optional(),
    is_opt_out_from_scheme: z
      .string()
      .min(1, "Opt-out from Scheme is required"),
    entrepreneur_type: z.string().min(1, "Entrepreneur Type is required"),
    employment_generation: z.string().min(1, "This field is required"),
    value_added_variety: z
      .array(z.string())
      .min(1, "At least one value-added variety must be selected"),
    export_oriented_copy: z.any().optional(),
    marketing_tieup_details: z.any().optional(),
    turnover_proof: z.any().optional(),
  })
  .superRefine((data, ctx) => {
    // if (data.export_oriented === "yes") {
    //   if (!data.type_of_product_exported) {
    //     ctx.addIssue({
    //       path: ["type_of_product_exported"],
    //       code: z.ZodIssueCode.custom,
    //       message: "Type of product exported is required",
    //     });
    //   }

    //   const hasFile = data.export_oriented_copy instanceof File;
    //   const hasUrl =
    //     typeof data.export_oriented_copy === "string" &&
    //     data.export_oriented_copy.length > 0;

    //   if (!hasFile && !hasUrl) {
    //     ctx.addIssue({
    //       path: ["export_oriented_copy"],
    //       code: z.ZodIssueCode.custom,
    //       message: "Export Oriented copy is required",
    //     });
    //   }
    // }

    if (data.value_added_variety.includes("Any Other (Specify)")) {
      if (!data?.other_variety_specify) {
        ctx.addIssue({
          path: ["other_variety_specify"],
          code: z.ZodIssueCode.custom,
          message: "Specify the other variety",
        });
      }
    }

    if (data.is_tie_up_made === "Yes" && Array.isArray(data.tie_up_types)) {
      if (data.tie_up_types.includes("Marketing Tie Up Arrangements")) {
        const hasFile = data.marketing_tieup_details instanceof File;
        const hasUrl =
          typeof data.marketing_tieup_details === "string" &&
          data.marketing_tieup_details.length > 0;

        if (!hasFile && !hasUrl) {
          ctx.addIssue({
            path: ["marketing_tieup_details"],
            code: z.ZodIssueCode.custom,
            message: "Marketing tieup details document is required",
          });
        }
      }
      if (data.tie_up_types.includes("Own Manufacturing Industries")) {
        if (!data.backward_raw_material_source) {
          ctx.addIssue({
            path: ["backward_raw_material_source"],
            code: z.ZodIssueCode.custom,
            message: "Source of raw material is required",
          });
        }
        if (!data.backward_labour_availability) {
          ctx.addIssue({
            path: ["backward_labour_availability"],
            code: z.ZodIssueCode.custom,
            message: "Labour availability is required",
          });
        }
      }
    }

    if (data.willingness_to_switch === "yes") {
      if (!data?.willingness_description) {
        ctx.addIssue({
          path: ["willingness_description"],
          code: z.ZodIssueCode.custom,
          message: "Description is required",
        });
      }
    }
  });

const PriorityDetails = ({ data }) => {
  const [filePreviews, setFilePreviews] = useState({});
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema),
    defaultValues: {
      is_tie_up_made: data?.priority?.is_tie_up_made ?? "",
      tie_up_types: Array.isArray(data?.priority?.tie_up_types)
        ? data?.priority?.tie_up_types
        : typeof data?.priority?.tie_up_types === "string"
        ? JSON.parse(data?.priority?.tie_up_types)
        : [],
      export_oriented: data?.priority?.export_oriented ?? "",
      type_of_product_exported: data?.priority?.type_of_product_exported ?? "",
      value_added_variety: Array.isArray(data?.priority?.value_added_variety)
        ? data?.priority?.value_added_variety
        : data?.priority?.value_added_variety
        ? [data?.priority?.value_added_variety]
        : [],
      turnover: data?.priority?.turnover ?? "",
      cluster_approach: data?.priority?.cluster_approach ?? "",
      willingness_to_switch: data?.priority?.willingness_to_switch ?? "",
      other_variety_specify: data?.priority?.other_variety_specify ?? "",
      entrepreneur_type: data?.priority?.entrepreneur_type ?? "",
      employment_generation: data?.priority?.employment_generation ?? "",
      backward_raw_material_source:
        data?.priority?.backward_raw_material_source ?? "",
      backward_labour_availability:
        data?.priority?.backward_labour_availability ?? "",
      export_oriented_copy: data?.priority?.export_oriented_copy ?? undefined,
      marketing_tieup_details:
        data?.priority?.marketing_tieup_details ?? undefined,
      turnover_proof: data?.priority?.turnover_proof ?? undefined,
      willingness_description: data?.priority?.willingness_description ?? "",
      is_opt_out_from_scheme: data?.priority?.is_opt_out_from_scheme ?? "",
    },
  });

  const formData = watch();

  useEffect(() => {
    if (data?.priority) {
      const previews = {
        export_oriented_copy: data?.priority?.export_oriented_copy ?? undefined,
        marketing_tieup_details:
          data?.priority?.marketing_tieup_details ?? undefined,
        turnover_proof: data?.priority?.turnover_proof ?? undefined,
      };
      setFilePreviews(previews);
    }
  }, [data]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else if (Array.isArray(formData[key])) {
            submitData.append(key, JSON.stringify(formData[key]));
          } else {
            submitData.append(key, formData[key]);
          }
        }
      });

      submitData.append("id", data?.id || "");

      let response = await api.post("/modernization/priority", submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      searchParams.delete("tab");
      // nav(
      //   {
      //     pathname: location.pathname,
      //     search: searchParams.toString(),
      //   },
      //   { replace: true }
      // );

      dispatch(OldConventionalUserInitiatedNav(true));
      if (buttonType === "next") {
        dispatch(OldConventionalCurrentTab("purchase_detail"));
      }
      dispatch(OldConventionalData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const onSubmitError = (errors) => {
    const firstErrorField = Object.keys(errors)[0];
    if (firstErrorField) {
      setFocus(firstErrorField);
    }
  };

  // Check if a tie-up type is selected
  const isTieUpTypeSelected = (type) => {
    return (
      Array.isArray(formData.tie_up_types) &&
      formData.tie_up_types.includes(type)
    );
  };

  const isOtherVarietySelected = (type) => {
    return (
      Array.isArray(formData.value_added_variety) &&
      formData.value_added_variety.includes(type)
    );
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const onSubmitClick = async (e, type) => {
    e.preventDefault();
    setButtonType(type); // Save button type first
    console.log("🖱️ Submit button clicked. Type:", type);

    const isValid = await trigger();
    console.log("✅ Validation result:", isValid);
    console.log("⚠️ Validation errors:", errors);

    if (isValid) {
      console.log("🎯 Form is valid, calling handleSubmitSpv...");
      // await form.handleSubmit(handleSubmitSpv)();
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit, onSubmitError)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Additional Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="is_tie_up_made" required>
                Whether you have made any tie-up arrangement with any marketing
                agency / running an own industry{" "}
              </Label>
              <Select
                value={formData.is_tie_up_made || ""}
                onValueChange={(value) => {
                  handleInputChange("is_tie_up_made", value);
                  if (value === "No") {
                    handleInputChange("tie_up_types", []);
                  }
                }}
              >
                <SelectTrigger
                  className={errors.is_tie_up_made ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.is_tie_up_made && (
                <p className="text-sm text-red-500">
                  {errors.is_tie_up_made.message}
                </p>
              )}
            </div>

            {formData.is_tie_up_made === "Yes" && (
              <div className="space-y-2 mt-5">
                <Label required>Type of Tie-up Made</Label>
                <MultiSelect
                  options={[
                    {
                      id: "Marketing Tie Up Arrangements",
                      name: "Marketing Tie Up Arrangements",
                    },
                    {
                      id: "Own Manufacturing Industries",
                      name: "Own Manufacturing Industries",
                    },
                  ]}
                  value={formData.tie_up_types || []}
                  onChange={(value) => handleInputChange("tie_up_types", value)}
                  placeholder="Select Tie-up Types"
                />
                {errors.tie_up_types && (
                  <p className="text-sm text-red-500">
                    {errors.tie_up_types.message}
                  </p>
                )}
              </div>
            )}

            {isTieUpTypeSelected("Own Manufacturing Industries") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="backward_raw_material_source" required>
                    Source of Raw Material
                  </Label>
                  <Input
                    id="backward_raw_material_source"
                    value={formData.backward_raw_material_source || ""}
                    onChange={(e) => {
                      handleInputChange(
                        "backward_raw_material_source",
                        e.target.value
                      );
                    }}
                    placeholder="Enter Source of Raw material"
                    className={
                      errors.backward_raw_material_source
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {errors.backward_raw_material_source && (
                    <p className="text-sm text-red-500">
                      {errors.backward_raw_material_source.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="backward_labour_availability" required>
                    Availability of Labour
                  </Label>
                  <Input
                    id="backward_labour_availability"
                    value={formData.backward_labour_availability || ""}
                    onChange={(e) => {
                      handleInputChange(
                        "backward_labour_availability",
                        e.target.value
                      );
                    }}
                    placeholder="Enter Availability of labour"
                    className={
                      errors.backward_labour_availability
                        ? "border-red-500"
                        : ""
                    }
                  />
                  {errors.backward_labour_availability && (
                    <p className="text-sm text-red-500">
                      {errors.backward_labour_availability.message}
                    </p>
                  )}
                </div>
              </>
            )}

            {isTieUpTypeSelected("Marketing Tie Up Arrangements") && (
              <div className="space-y-2">
                <Label htmlFor="marketing_tieup_details" required>
                  Marketing Tieup Details Document
                </Label>
                <div className="border-2 border-dashed rounded-lg p-4 text-center">
                  <Input
                    id="marketing_tieup_details"
                    type="file"
                    accept=".jpg,.png,.jpeg,.pdf"
                    onChange={(e) =>
                      handleFileUpload(
                        "marketing_tieup_details",
                        e.target.files[0]
                      )
                    }
                    className={
                      errors.marketing_tieup_details ? "border-red-500" : ""
                    }
                  />

                  <FilePreview
                    fieldName="marketing_tieup_details"
                    preview={filePreviews.marketing_tieup_details}
                    file={
                      formData.marketing_tieup_details instanceof File
                        ? formData.marketing_tieup_details
                        : null
                    }
                    existingFile={
                      typeof filePreviews.marketing_tieup_details === "string"
                        ? filePreviews.marketing_tieup_details
                        : null
                    }
                    onRemove={handleRemoveFile}
                  />

                  {errors.marketing_tieup_details && (
                    <p className="text-sm text-red-500">
                      {errors.marketing_tieup_details.message}
                    </p>
                  )}

                  <p className="ms-1 text-[12px] text-muted-foreground mt-2">
                    Supported: JPG, JPEG, PNG, PDF (max 2MB)
                  </p>
                </div>
              </div>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="export_oriented" required>
                Whether Engaged in Export Oriented Production
              </Label>
              <Select
                value={formData.export_oriented || ""}
                onValueChange={(value) =>
                  handleInputChange("export_oriented", value)
                }
              >
                <SelectTrigger
                  className={errors.export_oriented ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.export_oriented && (
                <p className="text-sm text-red-500">
                  {errors.export_oriented.message}
                </p>
              )}
            </div>

            {/* {formData.export_oriented === "yes" && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="type_of_product_exported" required>
                    Type of Product Exported
                  </Label>
                  <Input
                    id="type_of_product_exported"
                    value={formData.type_of_product_exported || ""}
                    onChange={(e) => {
                      handleInputChange(
                        "type_of_product_exported",
                        e.target.value
                      );
                    }}
                    placeholder="Enter type of product"
                    className={
                      errors.type_of_product_exported ? "border-red-500" : ""
                    }
                  />
                  {errors.type_of_product_exported && (
                    <p className="text-sm text-red-500">
                      {errors.type_of_product_exported.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="export_oriented_copy" required>
                    Upload Document of Evidence
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4 text-center">
                    <Input
                      id="export_oriented_copy"
                      type="file"
                      accept=".jpg,.png,.jpeg,.pdf"
                      onChange={(e) =>
                        handleFileUpload(
                          "export_oriented_copy",
                          e.target.files[0]
                        )
                      }
                      className={
                        errors.export_oriented_copy ? "border-red-500" : ""
                      }
                    />

                    <FilePreview
                      fieldName="export_oriented_copy"
                      preview={filePreviews.export_oriented_copy}
                      file={
                        formData.export_oriented_copy instanceof File
                          ? formData.export_oriented_copy
                          : null
                      }
                      existingFile={
                        typeof filePreviews.export_oriented_copy === "string"
                          ? filePreviews.export_oriented_copy
                          : null
                      }
                      onRemove={handleRemoveFile}
                    />

                    {errors.export_oriented_copy && (
                      <p className="text-sm text-red-500">
                        {errors.export_oriented_copy.message}
                      </p>
                    )}

                    <p className="ms-1 text-[12px] text-muted-foreground mt-2">
                      Supported: JPG, JPEG, PNG, PDF (max 2MB)
                    </p>
                  </div>
                </div>
              </>
            )} */}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="turnover" required>
                Business Turnover for the financial Year (in Rs.)
              </Label>
              <div className="flex gap-2">
                <Input
                  id="turnover"
                  value={formData.turnover || ""}
                  onChange={(e) => {
                    const value = e.target.value
                      .replace(/\D/g, "")
                      .slice(0, 10);
                    handleInputChange("turnover", value);
                  }}
                  placeholder="Enter annual turnover in Rs."
                  className={errors.turnover ? "border-red-500" : ""}
                />
              </div>
              {errors.turnover && (
                <p className="text-sm text-red-500">
                  {errors.turnover.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="turnover_proof">
                Turnover proof to be enclosed{" "}
              </Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <Input
                  id="turnover_proof"
                  type="file"
                  accept=".jpg,.png,.jpeg,.pdf"
                  onChange={(e) =>
                    handleFileUpload("turnover_proof", e.target.files[0])
                  }
                  className={errors.turnover_proof ? "border-red-500" : ""}
                />

                {/* Add FilePreview component for turnover_proof */}
                <FilePreview
                  fieldName="turnover_proof"
                  preview={filePreviews.turnover_proof}
                  file={
                    formData.turnover_proof instanceof File
                      ? formData.turnover_proof
                      : null
                  }
                  existingFile={
                    typeof filePreviews.turnover_proof === "string"
                      ? filePreviews.turnover_proof
                      : null
                  }
                  onRemove={handleRemoveFile}
                />

                {errors.turnover_proof && (
                  <p className="text-sm text-red-500">
                    {errors.turnover_proof.message}
                  </p>
                )}

                <p className="ms-1 text-[12px] text-muted-foreground mt-2">
                  Supported: JPG, JPEG, PNG, PDF (max 2MB)
                </p>
              </div>
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="cluster_approach"
                className="flex items-center gap-3"
                required
              >
                <span>Whether Loom Shed is Located in Cluster </span>
                <Popover>
                  <PopoverTrigger>
                    <HelpCircle className="text-primary" />
                  </PopoverTrigger>
                  <PopoverContent>
                    <span className="font-bold">Cluster</span>: If your shed is
                    located in a place where other loom sheds are in nearby then
                    select Cluster.
                    <br />
                    <br />
                    <span className="font-bold">Scattered</span>: If Your shed
                    is the only one or one of very few in your village or area
                    then Select Scattered.
                  </PopoverContent>
                </Popover>
              </Label>
              <Select
                value={formData.cluster_approach || ""}
                onValueChange={(value) =>
                  handleInputChange("cluster_approach", value)
                }
              >
                <SelectTrigger
                  className={errors.cluster_approach ? "border-red-500" : ""}
                >
                  <SelectValue placeholder="Select Cluster or Scattered" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cluster">Cluster</SelectItem>
                  <SelectItem value="Scattered">Scattered</SelectItem>
                </SelectContent>
              </Select>
              {errors.cluster_approach && (
                <p className="text-sm text-red-500">
                  {errors.cluster_approach.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="willingness_to_switch" required>
                Willingness to Switch Over to Commercial Production and
                Innovations
              </Label>
              <Select
                value={formData.willingness_to_switch || ""}
                onValueChange={(value) =>
                  handleInputChange("willingness_to_switch", value)
                }
              >
                <SelectTrigger
                  className={
                    errors.willingness_to_switch ? "border-red-500" : ""
                  }
                >
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
              {errors.willingness_to_switch && (
                <p className="text-sm text-red-500">
                  {errors.willingness_to_switch.message}
                </p>
              )}
            </div>

            {formData.willingness_to_switch === "yes" && (
              <div className="space-y-2">
                <Label htmlFor="willingness_description" required>
                  Enter Description
                </Label>
                <Textarea
                  id="willingness_description"
                  {...register("willingness_description")}
                  rows={2}
                  className={
                    errors.willingness_description ? "border-red-500" : ""
                  }
                />
                {errors.willingness_description && (
                  <p className="text-sm text-red-500">
                    {errors.willingness_description.message}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="entrepreneur_type" required>
                Whether the applicant is a New Entrepreneur
              </Label>
              <Select
                value={formData.entrepreneur_type || ""}
                onValueChange={(value) =>
                  handleInputChange("entrepreneur_type", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select any Existing or Start Up" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Existing">Existing</SelectItem>
                  <SelectItem value="Start Up">Start Up</SelectItem>
                </SelectContent>
              </Select>
              {errors.entrepreneur_type && (
                <p className="text-sm text-red-500">
                  {errors.entrepreneur_type.message}
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label required>Whether Engaged in Value Added Varieties </Label>
              <MultiSelect
                options={[
                  { id: "Checked", name: "Checked" },
                  { id: "Stripes", name: "Stripes" },
                  { id: "Dobby", name: "Dobby" },
                  { id: "Jacquard", name: "Jacquard" },
                  { id: "Any Other (Specify)", name: "Any Other (Specify)" },
                ]}
                value={formData.value_added_variety || []}
                onChange={(value) =>
                  handleInputChange("value_added_variety", value)
                }
                placeholder="Select Value Added Varieties"
              />
              {errors.value_added_variety && (
                <p className="text-sm text-red-500">
                  {errors.value_added_variety.message}
                </p>
              )}
            </div>

            {isOtherVarietySelected("Any Other (Specify)") && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="other_variety_specify" required>
                    Specify Other Value added varieties
                  </Label>
                  <Input
                    id="other_variety_specify"
                    value={formData.other_variety_specify || ""}
                    onChange={(e) => {
                      handleInputChange(
                        "other_variety_specify",
                        e.target.value
                      );
                    }}
                    className={
                      errors.other_variety_specify ? "border-red-500" : ""
                    }
                  />
                  {errors.other_variety_specify && (
                    <p className="text-sm text-red-500">
                      {errors.other_variety_specify.message}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="is_opt_out_from_scheme" required>
                Are you a weaver of powerloom co-operative society who prefer to
                come out from the scheme production
              </Label>
              <Select
                value={formData.is_opt_out_from_scheme || ""}
                onValueChange={(value) =>
                  handleInputChange("is_opt_out_from_scheme", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select Yes or No" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="NA">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
              {errors.is_opt_out_from_scheme && (
                <p className="text-sm text-red-500">
                  {errors.is_opt_out_from_scheme.message}
                </p>
              )}
            </div>
          </div>

          <Separator />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employment_generation" required>
                Estimated Employment generation{" "}
              </Label>
              <Input
                id="employment_generation"
                value={formData.employment_generation || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  handleInputChange("employment_generation", value);
                }}
              />
              {errors.employment_generation && (
                <p className="text-sm text-red-500">
                  {errors.employment_generation.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={() => setButtonType("next")}
              disabled={isSubmitting}
              // onClick={(e) => onSubmitClick(e, "submit")}
              className="min-w-[150px]"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default PriorityDetails;
